import React from "react";
import { Link } from "react-router-dom";
import { Shirt, Sparkles, UtensilsCrossed, Hammer, Scissors, Palette, Home, Gem, ChevronRight } from "lucide-react";

/**
 * CategoryGrid — Jumia-style icon tiles for top categories.
 * 8 tiles, 4-up on desktop / 2-up on mobile. Each routes to a pre-filtered
 * Browse URL so users skip filter selection.
 */
const TILES = [
  { label: "Fashion & Ankara", icon: Shirt,           type: "product", q: "ankara fashion",     accent: "from-[#C05A3C]/15 to-[#C05A3C]/0",   ring: "ring-[#C05A3C]/30" },
  { label: "Beauty & Hair",    icon: Sparkles,        type: "product", q: "shea butter beauty", accent: "from-[#D9923B]/18 to-[#D9923B]/0",   ring: "ring-[#D9923B]/30" },
  { label: "Food & Spices",    icon: UtensilsCrossed, type: "product", q: "jollof spice food",  accent: "from-[#2A3D36]/15 to-[#2A3D36]/0",   ring: "ring-[#2A3D36]/30" },
  { label: "Art & Crafts",     icon: Palette,         type: "product", q: "art crafts african", accent: "from-[#1A2024]/12 to-[#1A2024]/0",   ring: "ring-[#1A2024]/30" },
  { label: "Jewellery",        icon: Gem,             type: "product", q: "jewellery beads",    accent: "from-[#D9923B]/15 to-[#D9923B]/0",   ring: "ring-[#D9923B]/30" },
  { label: "Hair & Braiding",  icon: Scissors,        type: "service", q: "braiding hair",      accent: "from-[#C05A3C]/15 to-[#C05A3C]/0",   ring: "ring-[#C05A3C]/30" },
  { label: "Catering",         icon: Home,            type: "service", q: "catering cooking",   accent: "from-[#2A3D36]/15 to-[#2A3D36]/0",   ring: "ring-[#2A3D36]/30" },
  { label: "Handyman",         icon: Hammer,          type: "service", q: "handyman repair",    accent: "from-[#1A2024]/12 to-[#1A2024]/0",   ring: "ring-[#1A2024]/30" },
];

const CategoryGrid = () => (
  <section className="container-narrow py-12 sm:py-14" data-testid="category-grid">
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <div className="label-overline text-[#C05A3C] mb-2">Shop by category</div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">
          Find what you&apos;re looking for in seconds.
        </h2>
      </div>
      <Link
        to="/browse"
        className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#1A1A1A] hover:text-[#C05A3C] transition-colors"
        data-testid="category-grid-browse-all"
      >
        Browse everything <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {TILES.map(({ label, icon: Icon, type, q, accent, ring }) => (
        <Link
          key={label}
          to={`/browse?type=${type}&q=${encodeURIComponent(q)}`}
          data-testid={`category-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`group relative overflow-hidden rounded-xl bg-white border border-black/10 hover:border-transparent hover:ring-2 ${ring} transition-all duration-300 p-4 sm:p-5 hover:-translate-y-0.5`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} aria-hidden />
          <div className="relative flex items-center gap-3">
            <div className="shrink-0 w-11 h-11 rounded-lg bg-[#F3EFE9] grid place-items-center group-hover:bg-white transition-colors">
              <Icon className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm sm:text-base leading-tight text-[#1A1A1A] group-hover:text-[#C05A3C] transition-colors">
                {label}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
                {type === "service" ? "Local services" : "Products"}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export default CategoryGrid;
